import React, { useState, useRef, useEffect } from 'react';

interface DatePickerProps {
  label: string;
  value: string;
  onChange: (date: string) => void;
  minDate?: string;
  icon?: React.ReactNode;
  id?: string;
  alignRight?: boolean;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export default function DatePicker({ label, value, onChange, minDate, icon, id, alignRight }: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const parsedDate = value ? new Date(value + 'T00:00:00') : new Date();
  const [currentMonth, setCurrentMonth] = useState(parsedDate.getMonth());
  const [currentYear, setCurrentYear] = useState(parsedDate.getFullYear());

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      setTimeout(() => {
        dropdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInMonth = (month: number, year: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month: number, year: number) => new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleSelectDate = (day: number) => {
    const y = currentYear;
    const m = String(currentMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    const selected = `${y}-${m}-${d}`;
    if (minDate && selected < minDate) return; // Prevent selecting before minDate
    onChange(selected);
    setIsOpen(false);
  };

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);

  const days = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(<div key={`empty-${i}`} className="w-8 h-8" />);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const isSelected =
      value &&
      parsedDate.getDate() === d &&
      parsedDate.getMonth() === currentMonth &&
      parsedDate.getFullYear() === currentYear;

    const y = currentYear;
    const m = String(currentMonth + 1).padStart(2, '0');
    const dayStr = String(d).padStart(2, '0');
    const dateStr = `${y}-${m}-${dayStr}`;
    const isDisabled = minDate ? dateStr < minDate : false;

    days.push(
      <button
        key={d}
        type="button"
        disabled={isDisabled}
        onClick={() => handleSelectDate(d)}
        className={`w-8 h-8 flex items-center justify-center rounded-full text-sm transition-all duration-200 ${
          isSelected
            ? 'bg-accent text-white font-bold shadow-md transform scale-110'
            : isDisabled
              ? 'text-slate-300 cursor-not-allowed'
              : 'text-slate-700 hover:bg-accent/10 hover:text-accent font-medium'
        }`}
      >
        {d}
      </button>
    );
  }

  const displayDate = value
    ? parsedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : 'Select Date';

  return (
    <div className="relative flex-1" ref={containerRef}>
      <div
        id={id}
        onClick={() => setIsOpen(!isOpen)}
        className="relative h-14 sm:h-[62px] bg-white border border-transparent hover:border-accent/40 focus-within:border-accent/60 rounded-xl px-3.5 transition-all flex items-center shadow-inner cursor-pointer"
      >
        <span className="absolute left-3.5 top-1.5 text-[8.5px] font-black uppercase tracking-widest text-slate-500">
          {label}
        </span>
        {icon || (
          <svg
            className="absolute left-3.5 bottom-3.5 text-accent-light"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        )}
        <div
          className="w-full bg-transparent text-slate-800 rounded-none text-[13.5px] sm:text-sm font-bold focus:outline-none pt-3 sm:pt-4 pr-1 truncate"
          style={{ paddingLeft: '26px' }}
        >
          {displayDate}
        </div>
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className={`absolute top-full ${alignRight ? 'right-0 sm:left-0 sm:right-auto' : 'left-0'} mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 p-4 z-50 w-[280px] animate-fadeInUp`}
        >
          <div className="flex justify-between items-center mb-4">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1.5 rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <div className="font-bold text-slate-800 text-sm">
              {MONTHS[currentMonth]} {currentYear}
            </div>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1.5 rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS.map((day) => (
              <div
                key={day}
                className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">{days}</div>
        </div>
      )}
    </div>
  );
}
